import { api } from '../../core/network/api';
import type { ItemCopy } from '../config/item-copy';

/** Quick-use consumables REST surface — plain wrappers, same convention as contracts-api.ts. */

/**
 * Mirrors the backend's `ConsumableEffect`. A union rather than `string` so
 * that adding a kind server-side surfaces here as a typecheck failure in
 * `consumableEffectLine` instead of a card that silently describes nothing.
 */
export type ConsumableEffect =
  | 'HEAL_OVER_TIME'
  | 'RESOURCE_RESTORE'
  | 'STAMINA_RESTORE'
  | 'WEAPON_EDGE';

export interface ConsumableDefRow {
  id: string;
  name: string;
  slot: number;
  effect: ConsumableEffect;
  priceWax: number;
  weightKg: number;
  maxCharges: number;
  channelMs: number;
  cooldownMs: number;
  magnitude: number;
  durationMs: number;
  usableBy: string[];
  /**
   * The three lines the detail sheet reads — served verbatim off the backend
   * def. Optional because a client can outlive the server build it talks to,
   * and `ui/item-detail.ts` falls back rather than throwing; a deploy is two
   * containers and one of them is briefly the older one.
   */
  copy?: ItemCopy;
}

export interface SatchelChargeRow {
  defId: string;
  slot: number;
  charges: number;
}

export interface ConsumablesResponse {
  catalog: ConsumableDefRow[];
  /** defId -> charges owned, account-level. */
  stock: Record<string, number>;
  /** What the queried class would carry into a run right now. */
  satchel: SatchelChargeRow[];
}

/**
 * `characterClass` only decides the `satchel` preview — the catalog and stock
 * are account-level, so passing nothing is valid and simply omits the preview.
 */
export const getConsumables = (characterClass?: string): Promise<ConsumablesResponse> =>
  api.get(`/consumables${characterClass ? `?characterClass=${encodeURIComponent(characterClass)}` : ''}`);

export const purchaseConsumable = (defId: string, quantity: number): Promise<{ stock: Record<string, number>; softBalance: number }> =>
  api.post('/consumables/purchase', { defId, quantity });
